import { kioskFetch } from './kioskFetch';

/**
 * The pay-card proxy id's configured prefix — `GET /pay-card/proxy-id-prefix` → `{ prefix }`
 * [03-us-kpc-1e; decision 0423 § *Decision*, point 5].
 *
 * **The server sends it, because the Operator ruled it so on 2026-10-05** (0423, Q6: *"the server sends
 * it"*), which closed OQ-151. The value lives in one place — the api's `KIOSK_PAYCARD_PROXY_ID_PREFIX` — and
 * this read is behind the kiosk credential, so it is neither a literal in a component nor a `VITE_` value
 * inlined into JavaScript any browser downloads.
 *
 * ⚠️ **Hand-written where the convention (§ 6) asks for generated types**, for the reason
 * `identityLookup.ts` records: `npm run api:types` reads `../iss-kiosk-api/openapi.json`, which the backend
 * does not emit yet. The shape is parsed at the boundary below instead.
 */

/**
 * Short, on purpose. The mask is a convenience, and a read that does not answer falls back to today's plain
 * entry with no error [03-us-kpc-1e] — so waiting longer only widens the window in which a candidate who
 * starts typing early freezes the fields in plain form anyway.
 */
const REQUEST_TIMEOUT_MS = 5_000;

/**
 * Digits, 1 to 15 long — the api's own rule for `KIOSK_PAYCARD_PROXY_ID_PREFIX` (`env.schema.ts`, cited in
 * 0423's sources). A prefix of 16 would leave no position to type into; anything else is not a prefix.
 */
const PREFIX_SHAPE = /^[0-9]{1,15}$/;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/**
 * Same shape as `arrivalMenuState.ts`'s `reportUnread` (`msg` · `alert` · detail). **Never the value**: a
 * console on a shared device is not where the prefix goes, even though the screen shows it.
 */
function reportUnread(alert: string, detail: Record<string, unknown>): void {
  console.error({
    msg: 'the pay-card screen could not read the proxy id prefix — both fields fall back to plain entry',
    alert,
    route: '/pay-card/proxy-id-prefix',
    ...detail,
  });
}

/**
 * Reads the configured prefix. **Never throws**, and every failure is `null`: the screen then offers
 * today's plain 16-digit entry with no error on screen [03-us-kpc-1e]. A 401 is not reported here —
 * `kioskFetch` already reports it and returns the device to the PIN pad.
 *
 * Complexity: one request, bounded by `REQUEST_TIMEOUT_MS`.
 */
export async function fetchProxyIdPrefix(): Promise<string | null> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}/pay-card/proxy-id-prefix`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (error: unknown) {
    reportUnread('pay_card_prefix_unreachable', {
      reason: error instanceof Error ? error.name : typeof error,
    });
    return null;
  }
  if (!response.ok) {
    if (response.status !== 401) {
      reportUnread('pay_card_prefix_rejected', { status: response.status });
    }
    return null;
  }

  const body: unknown = await response.json().catch(() => undefined);
  const prefix =
    typeof body === 'object' && body !== null && !Array.isArray(body)
      ? (body as Record<string, unknown>).prefix
      : undefined;
  if (typeof prefix !== 'string' || !PREFIX_SHAPE.test(prefix)) {
    reportUnread('pay_card_prefix_contract', {});
    return null;
  }
  return prefix;
}
